import { useState } from "react"
import { navigate } from "gatsby"
import {
  EXTERNAL_REFERRER,
  useUtmParameters,
  UTM_CAMPAIGN,
  UTM_SOURCE,
  UTM_TERM,
  UTM_CONTENT,
  UTM_MEDIUM,
} from "../cookieLogic/utmTrackingLogic"

// Zoho Forms allows exporting a created Form as HTML+CSS.
//
// However, they do not allow control over what happens after Form submission- You are always redirected to a Zoho
// thank-you or error page, meaning visitor lands outside of our website.
// They also do not allow setting fieldnames for Fields, but generate them automatically eg 'SingleLine',
// 'SingleLine1', 'Checkbox', 'Checkbox1', etc.
//
// Therefore approach is to export the Form as HTML+CSS and use from it only the Form submission URL, plus their
// auto-generated fieldnames with our own component, which then handles form submission and ignores the Zoho responses/redirects.
//
// Requirement is that first 5 fields created in Zoho Form Builder are not shown to User and are mapped like:
// SingleLine - form source
// SingleLine1 - external_referer
// SingleLine2 - utm_source
// SingleLine3 - utm_campaign
// SingleLine4 - utm_term
//

type ZohoFormLogicType<T> = {
  formIdentifier: string
  zohoSubmitUrl: string
  successRedirectUrl: string
  requiredFieldValidationMessage: string
  validEmailValidationMessage: string
  otherSubmitError?: string
  submitErrorMessage?: string
  zohoFormFields: ZohoFormFields<T>
}

type ZohoFormHandlers<T> = {
  values: Partial<T>
  errors: FormErrors<T>
  isFetching: boolean
  submitError: string
  setValue: (name: string, value: any) => void
  clearError: (name: string) => void
  doSubmit: () => void
  doSubmitAndDownloadDocument: (docPath: string, docName: string) => void
}

export type ZohoFormFields<T> = {
  [K in keyof T]: {
    zohoFieldName: string
    initialValue: T[K]
    required?: boolean
    runEmailValidation?: boolean
  }
}

export type FormErrors<T> = {
  [K in keyof T]?: string
}

const isValidEmail = (email: any) => {
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
    email
  )
}

export const dateAsZohoString = (date: Date) =>
  date
    .toLocaleDateString("en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-")

// used en-NZ because it matches required format by zoho forms (dd-MMM-yyyy). en-GB generates september as the only 4-letter month and form then stops working

export const useZohoFormLogic = <T>({
  formIdentifier,
  zohoSubmitUrl,
  successRedirectUrl,
  requiredFieldValidationMessage,
  validEmailValidationMessage,
  otherSubmitError,
  submitErrorMessage,
  zohoFormFields,
}: ZohoFormLogicType<T>): ZohoFormHandlers<T> => {
  const utmParams = useUtmParameters()

  // Extract initial values from Zoho Form Fields
  const initialValues: Partial<T> = {}
  let eachValueKey: keyof T
  for (eachValueKey in zohoFormFields) {
    initialValues[eachValueKey] = zohoFormFields[eachValueKey].initialValue
  }

  // Set initial values and initial errors
  const [values, setValues] = useState<Partial<T>>(initialValues)
  const [errors, setErrors] = useState<FormErrors<T>>({})
  const [isFetching, setFetching] = useState(false)
  const [submitError, setSubmitError] = useState("")

  // Handlers to be passed to Fields
  const setValue = (name: string, value?: string | boolean) => {
    setValues({
      ...values,
      [name]: value,
    })
  }

  const clearError = (name: string) => {
    setErrors({
      ...errors,
      [name]: undefined,
    })
  }

  // Validation
  const validateFormValues = (): boolean => {
    const validationErrors: FormErrors<T> = {}
    let hasError = false

    for (eachValueKey in zohoFormFields) {
      // Validate required fields
      if (zohoFormFields[eachValueKey].required && !values[eachValueKey]) {
        switch (eachValueKey) {
          case "workPermit":
            hasError = true
            setSubmitError(otherSubmitError)
            break
          default:
            hasError = true
            validationErrors[eachValueKey] = requiredFieldValidationMessage
        }
      }

      // Validate email fields
      if (zohoFormFields[eachValueKey].runEmailValidation) {
        if (!isValidEmail(values[eachValueKey])) {
          hasError = true
          validationErrors[eachValueKey] = validEmailValidationMessage
        }
      }
    }

    if (hasError) {
      setErrors(validationErrors)
      return false
    } else {
      return true
    }
  }

  // Create shape that Zoho is expecting
  const assembleFormData = (): FormData => {
    const formData = new FormData()
    // Required Zoho fields
    formData.append("zf_referrer_name", "")
    formData.append("zf_redirect_url", "")
    formData.append("zc_gad", "")

    // Identify which Form is submitting in Zoho + UTM tracking
    formData.append("SingleLine", formIdentifier)
    formData.append("SingleLine1", utmParams[EXTERNAL_REFERRER])
    formData.append("SingleLine2", utmParams[UTM_SOURCE])
    formData.append("SingleLine3", utmParams[UTM_CAMPAIGN])
    formData.append("SingleLine4", utmParams[UTM_TERM])
    formData.append("SingleLine5", utmParams[UTM_CONTENT])
    formData.append("SingleLine6", utmParams[UTM_MEDIUM])

    // Fields that User enters
    for (eachValueKey in zohoFormFields) {
      if (typeof values[eachValueKey] === "boolean") {
        if (values[eachValueKey]) {
          formData.append(zohoFormFields[eachValueKey].zohoFieldName, "on")
        }
      } else if (typeof values[eachValueKey] === "string") {
        formData.append(
          zohoFormFields[eachValueKey].zohoFieldName,
          String(values[eachValueKey])
        )
      } else if (values[eachValueKey] !== undefined) {
        formData.append(
          zohoFormFields[eachValueKey].zohoFieldName,
          values[eachValueKey] as any
        )
      }
    }

    return formData
  }

  const createZohoRequest = () => {
    return fetch(zohoSubmitUrl, {
      method: "POST",
      body: assembleFormData(),
      headers: new Headers(),
      mode: "no-cors",
    })
  }

  const navigateToSuccessUrl = () => navigate(successRedirectUrl)

  const handleZohoErrorResponse = err => {
    console.error("Form submission error: ", err)
    setSubmitError(submitErrorMessage)
    setFetching(false)
  }

  const doZohoSubmit = () => {
    createZohoRequest()
      .then(navigateToSuccessUrl)
      .catch(handleZohoErrorResponse)
  }

  const doZohoSubmitAndDownloadDocument = (
    docPath: string,
    docName: string
  ) => {
    createZohoRequest()
      .then(() => {
        const anchor = document.createElement("a")
        anchor.href = docPath
        anchor.target = "_blank"
        anchor.setAttribute("download", docName)
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
      })
      .then(navigateToSuccessUrl)
      .catch(handleZohoErrorResponse)
  }

  const doSubmit = () => {
    if (validateFormValues()) {
      setFetching(true)
      doZohoSubmit()
    }
  }

  const doSubmitAndDownloadDocument = (docPath: string, docName: string) => {
    if (validateFormValues()) {
      setFetching(true)
      doZohoSubmitAndDownloadDocument(docPath, docName)
    }
  }

  return {
    values,
    errors,
    isFetching,
    submitError,
    setValue,
    clearError,
    doSubmit,
    doSubmitAndDownloadDocument,
  }
}
